/** Build FP Lab authorise[] from selected page features + roleslists catalog. */

export type FpPageFeature = {
  pageName: string
  url: string
  menu?: string | null
  feature: string[]
  features?: Array<{ label: string; code: string }>
}

/**
 * Create/Edit staff checkbox menus. `false` hides the whole menu and its
 * pages/actions (parity with Backend-UI until Premium BM is integrated).
 */
export const STAFF_FORM_MENU_ENABLED: Readonly<Record<string, boolean>> = {
  'Premium BM Management': false,
}

export function isStaffFormMenuEnabled(menu: string): boolean {
  const key = menu.trim()
  if (Object.prototype.hasOwnProperty.call(STAFF_FORM_MENU_ENABLED, key)) {
    return STAFF_FORM_MENU_ENABLED[key]!
  }
  return true
}

/** Drop disabled menus (and every page/action under them) for staff forms. */
export function filterStaffFormPageFeatures(pageFeatures: FpPageFeature[]): FpPageFeature[] {
  return pageFeatures.filter((page) =>
    isStaffFormMenuEnabled((page.menu || '').trim() || 'Other'),
  )
}

/** Keep only checkbox keys that still exist in the (filtered) catalog. */
export function retainStaffFormSelections(
  selectedKeys: string[],
  pageFeatures: FpPageFeature[],
): string[] {
  const allowed = new Set<string>()
  for (const page of pageFeatures) {
    for (const f of page.features ?? []) {
      allowed.add(featureKey(page.pageName, f.label))
    }
  }
  return selectedKeys.filter((k) => allowed.has(k))
}

export type FpAuthorisePage = {
  pageName: string
  url: string
  feature: string[]
}

/** Stable checkbox id — same grant code may appear on many pages. */
export function featureKey(pageName: string, label: string): string {
  return `${pageName}\0${label}`
}

/** Stale Access Level label → current checkbox labels (same page). */
const AUTHORISE_LABEL_EXPAND: Record<string, readonly string[]> = {
  'Cheater Members': ["Member's mapping", "Member's IP mapping"],
}

export function selectedFromAuthorise(authorise: FpAuthorisePage[] | undefined | null): string[] {
  if (!Array.isArray(authorise)) return []
  const out: string[] = []
  for (const page of authorise) {
    const name = String(page?.pageName ?? '').trim()
    if (!name) continue
    for (const label of page?.feature ?? []) {
      if (typeof label !== 'string' || !label.length) continue
      const expanded = AUTHORISE_LABEL_EXPAND[label]
      if (expanded) {
        for (const next of expanded) out.push(featureKey(name, next))
      } else {
        out.push(featureKey(name, label))
      }
    }
  }
  return out
}

/** Build authorise[] from selected page+label keys (not from grant codes). */
export function selectedToAuthorise(
  pageFeatures: FpPageFeature[],
  selectedKeys: string[],
): FpAuthorisePage[] {
  const selected = new Set(selectedKeys)
  const out: FpAuthorisePage[] = []
  for (const page of pageFeatures) {
    const defs = page.features ?? []
    const labels = defs
      .filter((f) => selected.has(featureKey(page.pageName, f.label)))
      .map((f) => f.label)
    if (!labels.length) continue
    out.push({
      pageName: page.pageName,
      url: page.url || `/${page.pageName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
      feature: labels,
    })
  }
  return out
}

/** Old shared codes → page-scoped codes (match CM-API expandLegacySharedGrants). */
const LEGACY_SHARED_EXPAND: Record<string, readonly string[]> = {
  'surveillance.view': [
    'surveillance-beta.view',
    'surveillance-beta.clear-members',
    'surveillance-beta.clear-date',
    'surveillance-beta.generate-graph',
    'surveillance-new.view',
    'surveillance-new.clear-members',
    'surveillance-new.generate-graph',
  ],
  'bet-settings.view': [
    'max-limits.view',
    'volume-percent.view',
    'volume-percent-new.view',
  ],
  'limits.write': ['max-limits.edit', 'max-limits.save-all'],
  'ticker.cheater-members': [
    'bet-ticker.members-mapping',
    'bet-ticker.members-ip-mapping',
    'bet-ticker-previous.members-mapping',
    'bet-ticker-previous.members-ip-mapping',
    'bet-ticker-flag-final.members-mapping',
    'bet-ticker-flag-final.members-ip-mapping',
  ],
  'bet-ticker.cheater-members': [
    'bet-ticker.members-mapping',
    'bet-ticker.members-ip-mapping',
  ],
  'bet-ticker-previous.cheater-members': [
    'bet-ticker-previous.members-mapping',
    'bet-ticker-previous.members-ip-mapping',
  ],
  'bet-ticker-flag-final.cheater-members': [
    'bet-ticker-flag-final.members-mapping',
    'bet-ticker-flag-final.members-ip-mapping',
  ],
}

function expandLegacySharedGrantCodes(grants: string[]): Set<string> {
  const codes = new Set(grants)
  for (const g of grants) {
    const add = LEGACY_SHARED_EXPAND[g]
    if (add) for (const c of add) codes.add(c)
  }
  return codes
}

/**
 * Legacy hydrate when staff row has allow_access codes but empty authorise.
 * Same code on multiple pages still checks all — prefer authorise when present.
 * Also expands pre-page-scope shared codes so Surveillance / Volume Percent checkboxes hydrate.
 */
export function selectedFromGrantCodes(
  pageFeatures: FpPageFeature[],
  grants: string[],
): string[] {
  const codes = expandLegacySharedGrantCodes(grants)
  const out: string[] = []
  for (const page of pageFeatures) {
    for (const f of page.features ?? []) {
      if (codes.has(f.code)) out.push(featureKey(page.pageName, f.label))
    }
  }
  return out
}

/** Roleslists → PermissionMenu tree for existing checkbox UI. */
export function roleslistToMenus(pageFeatures: FpPageFeature[]) {
  const byMenu = new Map<string, Map<string, Array<{ code: string; label: string }>>>()
  for (const page of pageFeatures) {
    const menu = (page.menu || 'Other').trim() || 'Other'
    if (!byMenu.has(menu)) byMenu.set(menu, new Map())
    const roles = byMenu.get(menu)!
    const actions = (page.features ?? []).map((f) => ({ code: f.code, label: f.label }))
    if (actions.length) roles.set(page.pageName, actions)
  }
  return [...byMenu.entries()].map(([menu, roles]) => ({
    menu,
    roles: [...roles.entries()].map(([accessRole, actions]) => ({ accessRole, actions })),
  }))
}
